/**
 * DoorTickets — the one narrowing every surface on this door reads. Both
 * axes this entrance filters on (settings.state.ts's `acceptedTypeIds` and
 * `originFilter`) are applied here, once, over the live ticket index
 * (tickets.state.ts), and the counts pill, the admissions timeline and the
 * remaining breakdown are all projections of this same set — so the list an
 * operator opens cannot narrow differently from the number they tapped to
 * open it. Narrowing on one axis alone would count guests a sale lane never
 * sees as still outstanding.
 *
 * Domain-root, not inside a sub-feature folder: three sub-features read it
 * and none of them owns it. It reads sibling `.state.ts` modules the way
 * settings.state.ts already reads event.state.ts — a derivation over this
 * domain's own cells, with no I/O of its own; the alternative is the same
 * predicate written out once per consumer, which is exactly the divergence
 * this file exists to make impossible.
 *
 * `computed` is pull-based, so a closed overlay costs nothing: the
 * projections below re-run only while something on screen is reading them.
 */
import { computed } from "@mhmo91/schmancy/state";
import {
	acceptedTypeIds,
	isTicketAccepted,
	originFilter,
} from "./settings/settings.state.ts";
import {
	answeredTickets,
	type IndexedTicket,
} from "./tickets/tickets.state.ts";

/** Every ticket this entrance actually admits, on both filter axes; `null`
 *  while the ticket index has not answered, so no count reads as zero. */
export const doorTickets = computed<readonly IndexedTicket[] | null>(() => {
	const tickets = answeredTickets.get();
	if (tickets === null) return null;
	const accepted = acceptedTypeIds.get();
	const origin = originFilter.value;
	return [...tickets.byTicketId.values()].filter((t) =>
		isTicketAccepted(t, accepted, origin),
	);
});

/** This door's own scanned/remaining readout. */
export const doorTicketCounts = computed(() => {
	const tickets = doorTickets.get();
	if (tickets === null) return null;
	return {
		scanned: tickets.filter((t) => t.status === "redeemed").length,
		total: tickets.length,
	};
});
